/**
 * ?? STORAGE GUARD - Prote��o contra uso de localStorage para pedidos
 * 
 * ?? ARQUITETURA DEFINITIVA:
 * - Firebase é a ÚNICA fonte de verdade para ORDERS
 * - localStorage NÃO pode ser usado para pedidos em NENHUMA circunstância
 * - Este guard sobrescreve localStorage.setItem para bloquear tentativas
 * 
 * Padr�o iFood/Uber: pedidos NUNCA ficam em cache local
 */

// Chaves bloqueadas (relacionadas a orders)
const BLOCKED_KEYS = [
  'eambulante_orders',
  'eambulante-orders',
  'orders',
  'eambulante_orders_v1',
  'eambulante_orders_v2',
  'eambulante_orders_v3',
  'eambulante_orders_v4',
  'eambulante_orders_v5',
  'eambulante_orders_v6',
  'eambulante_active_order',
];

const BLOCKED_PATTERNS = [
  /^eambulante_orders/i,
  /^orders$/i,
  /pedido/i,
  /entrega.*status/i,
];

// Flag para identificar produ��o
const IS_PRODUCTION = typeof window !== 'undefined' && 
  (window.location.hostname.includes('web.app') || 
   window.location.hostname.includes('firebaseapp.com') ||
   window.location.hostname === 'e-ambulante.com');

// Contador de viola��es (para debugging)
let violationCount = 0;

/**
 * Verifica se uma chave est� bloqueada
 */
const isBlockedKey = (key: string): boolean => {
  if (BLOCKED_KEYS.includes(key)) return true;
  
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.test(key)) return true;
  }
  
  return false;
};

/**
 * Logger de viola��es
 */
const logViolation = (key: string, action: string) => {
  violationCount++;
  const stack = new Error().stack || '';
  
  console.error(`
?????? STORAGE GUARD - VIOLAÇÃO DETECTADA ??????
?????????????????????????????????????????????
  AÇÃO BLOQUEADA: ${action}
  CHAVE: ${key}
  VIOLAÇÃO #: ${violationCount}
  AMBIENTE: ${IS_PRODUCTION ? 'PRODUÇÃO' : 'DESENVOLVIMENTO'}
  
  ?? ORDERS NÃO PODEM SER SALVOS EM localStorage!
  ?? Firebase é a ÚNICA fonte de verdade!
  
  STACK TRACE:
  ${stack.split('\n').slice(2, 6).join('\n')}
?????????????????????????????????????????????
`);
  
  // Em produ��o, registrar no Firebase para an�lise
  if (IS_PRODUCTION && violationCount <= 5) {
    try {
      // Evitar flood - s� registrar as primeiras 5 viola��es
      fetch('/api/storage-violation', {
        method: 'POST',
        body: JSON.stringify({ key, action, timestamp: Date.now() }),
        headers: { 'Content-Type': 'application/json' }
      }).catch(() => {}); // Silencioso se falhar
    } catch {}
  }
};

/**
 * Instala o guard sobrescrevendo localStorage.setItem
 */
export const installStorageGuard = (): void => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }

  // Guardar refer�ncia original
  const originalSetItem = localStorage.setItem.bind(localStorage);
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);
  
  // Sobrescrever setItem
  localStorage.setItem = (key: string, value: string): void => {
    if (isBlockedKey(key)) {
      logViolation(key, 'setItem');
      // Nunca lançar: exceção em setItem quebra checkout e fluxos críticos em produção.
      // Bloqueio = não persistir; Firebase continua sendo a fonte de verdade.
      return;
    }
    
    // Chave permitida, continuar normalmente
    originalSetItem(key, value);
  };

  // Limpar dados antigos de orders
  const cleanupOldOrderData = () => {
    BLOCKED_KEYS.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          console.log(`??? [StorageGuard] Removendo dados de orders antigos: ${key}`);
          originalRemoveItem(key);
        }
      } catch {}
    });
  };

  // Executar limpeza na inicializa��o
  cleanupOldOrderData();

  console.log(`
? STORAGE GUARD ATIVADO
???????????????????????????????????????
  Firebase é a ÚNICA fonte de verdade para:
  . Pedidos (orders)
  . Status de entregas
  . Dados de motoboy
  
  localStorage bloqueado para:
  ${BLOCKED_KEYS.join('\n  ')}
???????????????????????????????????????
`);
};

/**
 * Verifica se h� dados de orders no localStorage (para debugging)
 */
export const checkForOrdersInStorage = (): boolean => {
  let found = false;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isBlockedKey(key)) {
      console.error(`?? [StorageGuard] Dados de orders encontrados em: ${key}`);
      found = true;
    }
  }
  
  return found;
};

/**
 * Remove todos os dados de orders do localStorage
 */
export const purgeOrdersFromStorage = (): number => {
  let removed = 0;
  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isBlockedKey(key)) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
      removed++;
      console.log(`??? [StorageGuard] Removido: ${key}`);
    } catch {}
  });
  
  return removed;
};

/**
 * Retorna estat�sticas do storage guard
 */
export const getStorageGuardStats = () => ({
  violationCount,
  isProduction: IS_PRODUCTION,
  blockedKeysCount: BLOCKED_KEYS.length,
  hasOrdersInStorage: checkForOrdersInStorage(),
});

// Auto-instalar em produ��o
if (typeof window !== 'undefined') {
  // Delay para garantir que localStorage est� dispon�vel
  setTimeout(() => {
    installStorageGuard();
  }, 0);
}

export default {
  install: installStorageGuard,
  check: checkForOrdersInStorage,
  purge: purgeOrdersFromStorage,
  stats: getStorageGuardStats,
};
